import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './OnboardingPreferences.css';

const BIBLE_TRANSLATIONS = [
  { value: 'ESV', label: 'ESV (English Standard Version)', description: 'Literal translation with modern English' },
  { value: 'NIV', label: 'NIV (New International Version)', description: 'Balance of accuracy and readability' },
  { value: 'NASB', label: 'NASB (New American Standard)', description: 'Highly accurate literal translation' },
  { value: 'KJV', label: 'KJV (King James Version)', description: 'Traditional English translation' },
  { value: 'NKJV', label: 'NKJV (New King James)', description: 'Modern update of KJV' },
  { value: 'NLT', label: 'NLT (New Living Translation)', description: 'Thought-for-thought translation' },
  { value: 'CSB', label: 'CSB (Christian Standard Bible)', description: 'Balance of accuracy and clarity' },
];

const OnboardingStep3Preferences = ({ onNext, onSkip, isLoading }) => {
  const { preferences, updatePreferences } = useAuth();
  const [formData, setFormData] = useState({
    bibleTranslation: 'ESV',
    competitiveMode: false,
    appUsageMode: 'tracking',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (preferences) {
      setFormData({
        bibleTranslation: preferences.bibleTranslation || 'ESV',
        competitiveMode: preferences.competitiveMode || false,
        appUsageMode: preferences.appUsageMode || 'tracking',
      });
    }
  }, [preferences]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      await updatePreferences(formData);
      onNext(formData);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      // Show error to user
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="onboarding-step preferences-step">
      <p>
        Let's customize your experience. These settings help us tailor the app to your needs and can be changed later.
      </p>

      <form onSubmit={handleSubmit} className="preferences-form">
        {/* Bible Translation */}
        <div className="form-group">
          <label className="form-label">
            <span className="label-text">Preferred Bible Translation</span>
            <span className="label-description">Choose your favorite translation for reading</span>
          </label>
          <div className="translation-grid">
            {BIBLE_TRANSLATIONS.map(translation => (
              <div 
                key={translation.value}
                className={`translation-option ${formData.bibleTranslation === translation.value ? 'selected' : ''}`}
                onClick={() => handleChange('bibleTranslation', translation.value)}
              >
                <div className="translation-name">{translation.label}</div>
                <div className="translation-description">{translation.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* App Usage Mode */}
        <div className="form-group">
          <label className="form-label">
            <span className="label-text">How do you plan to use the app?</span>
            <span className="label-description">This helps us show relevant features</span>
          </label>
          <div className="usage-options">
            <label className="option-card">
              <input 
                type="radio" 
                name="appUsageMode" 
                value="tracking"
                checked={formData.appUsageMode === 'tracking'}
                onChange={(e) => handleChange('appUsageMode', e.target.value)}
              />
              <div className="option-content">
                <div className="option-icon">📊</div>
                <div className="option-title">Tracking Only</div>
                <div className="option-description">Track reading done elsewhere (physical Bible, other apps)</div>
              </div>
            </label>

            <label className="option-card">
              <input 
                type="radio" 
                name="appUsageMode" 
                value="reading"
                checked={formData.appUsageMode === 'reading'}
                onChange={(e) => handleChange('appUsageMode', e.target.value)}
              />
              <div className="option-content">
                <div className="option-icon">📖</div>
                <div className="option-title">Reading in App</div>
                <div className="option-description">Read Bible chapters directly in the app</div>
              </div>
            </label>

            <label className="option-card">
              <input 
                type="radio" 
                name="appUsageMode" 
                value="both"
                checked={formData.appUsageMode === 'both'}
                onChange={(e) => handleChange('appUsageMode', e.target.value)}
              />
              <div className="option-content">
                <div className="option-icon">🔄</div>
                <div className="option-title">Both</div>
                <div className="option-description">Mix of tracking and in-app reading</div>
              </div>
            </label>
          </div>
        </div>

        {/* Competitive Mode */}
        <div className="form-group">
          <label className="form-label">
            <span className="label-text">Competitive Features</span>
            <span className="label-description">Enable leaderboards and comparisons with family/friends</span>
          </label>
          <div className="toggle-option">
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={formData.competitiveMode}
                onChange={(e) => handleChange('competitiveMode', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <div className="toggle-label">
              <span className="toggle-title">
                {formData.competitiveMode ? 'Enabled' : 'Disabled'}
              </span>
              <span className="toggle-description">
                {formData.competitiveMode 
                  ? 'Show rankings, progress comparisons, and achievements'
                  : 'Focus on personal progress without comparisons'
                }
              </span>
            </div>
          </div>
        </div>

        <div className="step-actions">
          <button 
            type="button"
            className="btn-secondary"
            onClick={onSkip}
            disabled={isLoading || isSaving}
          >
            Skip for Now
          </button>
          <button 
            type="submit"
            className="btn-primary"
            disabled={isLoading || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingStep3Preferences;
